import { isArray, isBoolean, omit, pick, union } from 'lodash-es';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import type { PermissionObject } from '@/types/auth.types';

import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';

interface Channel {
  label: string;
  value: string;
  icon: string;
  permissions: PermissionObject[];
  sources: string[];
}

interface Platform {
  value: string;
  channel?: string;
  source?: string;
  mediaType?: string;
}

interface BrandMediaType {
  value: string;
  channel?: string;
  source?: string;
  mediaTypes: string[];
  permissions?: PermissionObject[];
  featureFlags?: string[];
  skipChannelPermissions?: boolean;
}

export interface MediaSourcesOptions {
  sources?: string[];
  channels?: string[];
  mediaTypes?: string[];
  excludedSources?: string[];
  excludedChannels?: string[];
  excludedMediaTypes?: string[];
  excludedBrandMediaTypes?: string[];
  includeAiGenerated?: boolean;
  includeAiGeneratedSource?: boolean;
  includeCompetitive?: boolean;
  aiGeneratedVideoOnly?: boolean;
}

export const SOURCES = {
  OWNED: {
    label: 'Owned',
    value: 'OWNED',
  },
  UGC: {
    label: 'UGC',
    value: 'UGC',
  },
  UPLOADS: {
    label: 'Uploads',
    value: 'UPLOADS',
  },
  AI_GENERATED: {
    label: 'AI Generated',
    value: 'AI_GENERATED',
  },
  COMPETITIVE: {
    label: 'Competitive',
    value: 'COMPETITIVE',
  },
} as const;

export const CHANNELS: Record<string, Channel> = {
  FACEBOOK: {
    label: 'Facebook',
    value: 'FACEBOOK',
    icon: 'facebook',
    permissions: [BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS, USER.FACEBOOK.CAN_ACCESS_FB_INSIGHTS],
    sources: [SOURCES.OWNED.value],
  },
  INSTAGRAM: {
    label: 'Instagram',
    value: 'INSTAGRAM',
    icon: 'instagram',
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM, USER.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
    sources: [SOURCES.OWNED.value, SOURCES.UGC.value],
  },
  LINKEDIN: {
    label: 'LinkedIn',
    value: 'LINKEDIN',
    icon: 'linkedin',
    permissions: [
      BRAND.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS,
      USER.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS,
    ],
    sources: [SOURCES.OWNED.value],
  },
  PINTEREST: {
    label: 'Pinterest',
    value: 'PINTEREST',
    icon: 'pinterest',
    permissions: [BRAND.PINTEREST.CAN_ACCESS_PINT, USER.PINTEREST.CAN_ACCESS_PINT],
    sources: [SOURCES.OWNED.value],
  },
  THREADS: {
    label: 'Threads',
    value: 'THREADS',
    icon: 'threads',
    permissions: [
      BRAND.THREADS.CAN_ACCESS_THREADS_INSIGHTS,
      USER.THREADS.CAN_ACCESS_THREADS_INSIGHTS,
    ],
    sources: [SOURCES.OWNED.value],
  },
  TIKTOK: {
    label: 'TikTok',
    value: 'TIKTOK',
    icon: 'tiktok',
    permissions: [BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS, USER.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS],
    sources: [SOURCES.OWNED.value, SOURCES.UGC.value],
  },
  X: {
    label: 'X',
    value: 'X',
    icon: 'x',
    permissions: [
      BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS,
      USER.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS,
    ],
    sources: [SOURCES.OWNED.value],
  },
  YOUTUBE: {
    label: 'YouTube',
    value: 'YOUTUBE',
    icon: 'youtube',
    permissions: [
      BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS,
      USER.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS,
    ],
    sources: [SOURCES.OWNED.value],
  },
  SNAPCHAT: {
    label: 'Snapchat',
    value: 'SNAPCHAT',
    icon: 'snapchat',
    permissions: [
      BRAND.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS,
      USER.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS,
    ],
    sources: [SOURCES.OWNED.value],
  },
};

export const MEDIA_TYPES = {
  AUDIO: {
    label: 'Audio',
    value: 'AUDIO',
    platformFilters: { threads: { post_types: ['AUDIO'] } },
  },
  DOCUMENT: {
    label: 'Document',
    value: 'DOCUMENT',
    platformFilters: { linkedin: { post_types: ['DOCUMENT'] } },
  },
  IMAGE: {
    label: 'Image/Photo',
    value: 'IMAGE',
    platformFilters: {
      instagram: { post_types: ['IMAGE', 'CAROUSEL'] },
      facebook: { post_types: ['PHOTO', 'CAROUSEL'] },
      pinterest: { is_video: false },
      twitter: { post_types: ['PHOTO', 'CAROUSEL'] },
      linkedin: { post_types: ['IMAGE', 'MULTI_IMAGE'] },
      snapchat: { post_types: ['STORY', 'SAVED_STORY', 'SPOTLIGHT'] },
      threads: { post_types: ['IMAGE', 'CAROUSEL_ALBUM'] },
      tiktok: { media_type: ['PHOTO'] },
      uploads: { media_types: ['IMAGE'] },
      ai_generated: { media_types: ['IMAGE'] },
    },
  },
  INSTAGRAM_STORY: {
    label: 'Instagram Story',
    value: 'INSTAGRAM_STORY',
    platformFilters: { instagram_story_frame: {} },
  },
  LINK: {
    label: 'Link',
    value: 'LINK',
    platformFilters: {
      facebook: { post_types: ['LINK'] },
      linkedin: { post_types: ['LINK'] },
      twitter: { post_types: ['LINK'] },
    },
  },
  TEXT: {
    label: 'Text',
    value: 'TEXT',
    platformFilters: {
      threads: { post_types: ['TEXT_POST'] },
      facebook: { post_types: ['TEXT'] },
      linkedin: { post_types: ['TEXT'] },
      twitter: { post_types: ['TEXT'] },
    },
  },
  VIDEO: {
    label: 'Video',
    value: 'VIDEO',
    platformFilters: {
      instagram: { post_types: ['VIDEO', 'REEL'] },
      facebook: { post_types: ['VIDEO', 'REEL'] },
      linkedin: { post_types: ['VIDEO'] },
      tiktok: { media_type: ['VIDEO'] },
      pinterest: { is_video: true },
      twitter: { post_types: ['VIDEO'] },
      youtube: {},
      snapchat: { post_types: ['SPOTLIGHT'] },
      threads: { post_types: ['VIDEO'] },
      uploads: { media_types: ['VIDEO'] },
      ai_generated: { media_types: ['VIDEO'] },
    },
  },
} as const;

export const PLATFORMS: Record<string, Platform> = {
  INSTAGRAM: {
    value: 'instagram',
    channel: CHANNELS.INSTAGRAM.value,
  },
  INSTAGRAM_STORY: {
    value: 'instagram_story_frame',
    mediaType: MEDIA_TYPES.INSTAGRAM_STORY.value,
  },
  FACEBOOK: {
    value: 'facebook',
    channel: CHANNELS.FACEBOOK.value,
  },
  LINKEDIN: {
    value: 'linkedin',
    channel: CHANNELS.LINKEDIN.value,
  },
  TIKTOK: {
    value: 'tiktok',
    channel: CHANNELS.TIKTOK.value,
  },
  PINTEREST: {
    value: 'pinterest',
    channel: CHANNELS.PINTEREST.value,
  },
  X: {
    value: 'twitter',
    channel: CHANNELS.X.value,
  },
  YOUTUBE: {
    value: 'youtube',
    channel: CHANNELS.YOUTUBE.value,
  },
  THREADS: {
    value: 'threads',
    channel: CHANNELS.THREADS.value,
  },
  SNAPCHAT: {
    value: 'snapchat',
    channel: CHANNELS.SNAPCHAT.value,
  },
  UPLOADS: {
    value: 'uploads',
    source: SOURCES.UPLOADS.value,
  },
  AI_GENERATED: {
    value: 'ai_generated',
    source: SOURCES.AI_GENERATED.value,
  },
};

export const BRAND_MEDIA_TYPES: Record<string, BrandMediaType> = {
  // Owned / Facebook
  FACEBOOK_OWNED: {
    value: 'FACEBOOK_OWNED',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  },
  FACEBOOK_LINK: {
    value: 'FACEBOOK_LINK',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.LINK.value],
  },
  FACEBOOK_TEXT_LINK: {
    value: 'FACEBOOK_TEXT_LINK',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.LINK.value, MEDIA_TYPES.TEXT.value],
  },
  FACEBOOK_TEXT: {
    value: 'FACEBOOK_TEXT',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.TEXT.value],
  },
  // Owned / Instagram
  INSTAGRAM_OWNED: {
    value: 'INSTAGRAM_OWNED',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  },
  INSTAGRAM_OWNED_IGTV: {
    value: 'INSTAGRAM_OWNED_IGTV',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.VIDEO.value],
  },
  INSTAGRAM_STORY: {
    value: 'INSTAGRAM_STORY',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.INSTAGRAM_STORY.value],
  },
  // Owned / LinkedIn
  LINKEDIN_OWNED: {
    value: 'LINKEDIN_OWNED',
    channel: CHANNELS.LINKEDIN.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [
      MEDIA_TYPES.DOCUMENT.value,
      MEDIA_TYPES.IMAGE.value,
      MEDIA_TYPES.LINK.value,
      MEDIA_TYPES.TEXT.value,
      MEDIA_TYPES.VIDEO.value,
    ],
  },
  // Owned / Pinterest
  PINTEREST_OWNED: {
    value: 'PINTEREST_OWNED',
    channel: CHANNELS.PINTEREST.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  },
  // Owned / Threads
  THREADS_OWNED: {
    value: 'THREADS_OWNED',
    channel: CHANNELS.THREADS.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [
      MEDIA_TYPES.AUDIO.value,
      MEDIA_TYPES.IMAGE.value,
      MEDIA_TYPES.TEXT.value,
      MEDIA_TYPES.VIDEO.value,
    ],
  },
  // Owned / TikTok
  TIKTOK_OWNED: {
    value: 'TIKTOK_OWNED',
    channel: CHANNELS.TIKTOK.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  },
  // Owned / X
  TWITTER_OWNED: {
    value: 'TWITTER_OWNED',
    channel: CHANNELS.X.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  },
  TWITTER_OWNED_LINK: {
    value: 'TWITTER_OWNED_LINK',
    channel: CHANNELS.X.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.LINK.value],
  },
  TWITTER_OWNED_TEXT: {
    value: 'TWITTER_OWNED_TEXT',
    channel: CHANNELS.X.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.TEXT.value],
  },
  // Owned / YouTube
  YOUTUBE_OWNED: {
    value: 'YOUTUBE_OWNED',
    channel: CHANNELS.YOUTUBE.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.VIDEO.value],
  },
  // Owned / Snapchat
  SNAPCHAT_OWNED_STORY: {
    value: 'SNAPCHAT_OWNED_STORY',
    channel: CHANNELS.SNAPCHAT.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value],
  },
  SNAPCHAT_OWNED_SPOTLIGHT: {
    value: 'SNAPCHAT_OWNED_SPOTLIGHT',
    channel: CHANNELS.SNAPCHAT.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  },
  SNAPCHAT_OWNED_SAVED_STORY: {
    value: 'SNAPCHAT_OWNED_SAVED_STORY',
    channel: CHANNELS.SNAPCHAT.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value],
  },
  /* SNAPCHAT_OWNED_SNAP: {
    value: 'SNAPCHAT_OWNED_SNAP',
    channel: CHANNELS.SNAPCHAT.value,
    source: SOURCES.OWNED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
  }, */
  // UGC / Instagram
  INSTAGRAM_UGC: {
    value: 'INSTAGRAM_UGC',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.UGC.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE, USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  },
  INSTAGRAM_UGC_IGTV: {
    value: 'INSTAGRAM_UGC_IGTV',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.UGC.value,
    mediaTypes: [MEDIA_TYPES.VIDEO.value],
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE, USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  },
  INSTAGRAM_STORY_UGC: {
    value: 'INSTAGRAM_STORY_UGC',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.UGC.value,
    mediaTypes: [MEDIA_TYPES.INSTAGRAM_STORY.value],
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE, USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  },
  // UGC / TikTok
  TIKTOK_UGC: {
    value: 'TIKTOK_UGC',
    channel: CHANNELS.TIKTOK.value,
    source: SOURCES.UGC.value,
    mediaTypes: [MEDIA_TYPES.VIDEO.value],
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE, USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  },
  // Uploads
  UPLOADED: {
    value: 'UPLOADED',
    source: SOURCES.UPLOADS.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
    permissions: [BRAND.LIBRARY.CAN_ACCESS_LIBRARY],
  },
  // AI Generated
  AI_GENERATED: {
    value: 'AI_GENERATED',
    source: SOURCES.AI_GENERATED.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value],
    permissions: [BRAND.LIBRARY.CAN_ACCESS_LIBRARY, BRAND.VISION.CAN_GENERATE_AI_ALTERNATIVE_MEDIA],
  },
  // Competitive / Instagram
  INSTAGRAM_OTHER: {
    value: 'INSTAGRAM_OTHER',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
    skipChannelPermissions: true,
  },
  INSTAGRAM_OTHER_IGTV: {
    value: 'INSTAGRAM_OTHER_IGTV',
    channel: CHANNELS.INSTAGRAM.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.VIDEO.value],
    skipChannelPermissions: true,
  },
  // Competitive / Facebook
  FACEBOOK_OTHER: {
    value: 'FACEBOOK_OTHER',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
    skipChannelPermissions: true,
  },
  FACEBOOK_OTHER_TEXT: {
    value: 'FACEBOOK_OTHER_TEXT',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.TEXT.value],
    skipChannelPermissions: true,
  },
  FACEBOOK_OTHER_LINK: {
    value: 'FACEBOOK_OTHER_LINK',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.LINK.value],
    skipChannelPermissions: true,
  },
  FACEBOOK_OTHER_TL: {
    value: 'FACEBOOK_OTHER_TL',
    channel: CHANNELS.FACEBOOK.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.LINK.value, MEDIA_TYPES.TEXT.value],
    skipChannelPermissions: true,
  },
  // Competitive / X
  TWITTER_OTHER: {
    value: 'TWITTER_OTHER',
    channel: CHANNELS.X.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.IMAGE.value, MEDIA_TYPES.VIDEO.value],
    skipChannelPermissions: true,
  },
  TWITTER_OTHER_LINK: {
    value: 'TWITTER_OTHER_LINK',
    channel: CHANNELS.X.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.LINK.value],
    skipChannelPermissions: true,
  },
  TWITTER_OTHER_TEXT: {
    value: 'TWITTER_OTHER_TEXT',
    channel: CHANNELS.X.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.TEXT.value],
    skipChannelPermissions: true,
  },
  // Competitive / YouTube
  YOUTUBE_OTHER: {
    value: 'YOUTUBE_OTHER',
    channel: CHANNELS.YOUTUBE.value,
    source: SOURCES.COMPETITIVE.value,
    mediaTypes: [MEDIA_TYPES.VIDEO.value],
    skipChannelPermissions: true,
  },
};

export function useMediaSources(options: MaybeRefOrGetter<MediaSourcesOptions> = {}) {
  const identityStore = useIdentityStore();
  const flagStore = useFlagStore();

  // Constellation Projects (AI_GENERATION boards) accept still images from any source plus
  // AI-generated video. `aiGeneratedVideoOnly` keeps only IMAGE for every source and allows VIDEO
  // solely from AI-generated media — and only when the brand can generate it, so the Video
  // media-type option is hidden otherwise. It implies `includeAiGenerated` because AI-generated
  // media is the only source that may contribute video; without it there would be no video source.
  const aiGeneratedVideoOnly = computed(() => Boolean(toValue(options)?.aiGeneratedVideoOnly));
  const includeAiGenerated = computed(
    () => Boolean(toValue(options)?.includeAiGenerated) || aiGeneratedVideoOnly.value,
  );
  const includeAiGeneratedSource = computed(() =>
    Boolean(toValue(options)?.includeAiGeneratedSource),
  );
  const includeCompetitive = computed(() => Boolean(toValue(options)?.includeCompetitive));

  const canAccessAiVideo = computed(
    () =>
      identityStore.guard(BRAND.VISION.CAN_ACCESS_VIDEO_GENERATION) &&
      identityStore.guard(USER.VISION.CAN_ACCESS_VIDEO_GENERATION),
  );

  function restrictToConstellationMediaTypes(mediaTypes: string[], source: string | undefined) {
    const isAiGenerated = source === SOURCES.AI_GENERATED.value;
    const allowed: string[] = [MEDIA_TYPES.IMAGE.value];
    if (isAiGenerated && canAccessAiVideo.value) {
      allowed.push(MEDIA_TYPES.VIDEO.value);
    }
    // The AI_GENERATED catalog entry only lists IMAGE, so union pulls in VIDEO when allowed.
    const withAiVideo = isAiGenerated ? union(mediaTypes, allowed) : mediaTypes;
    return withAiVideo.filter((mediaType) => allowed.includes(mediaType));
  }

  const brandMediaTypesWithPermission = computed(() =>
    Object.values(BRAND_MEDIA_TYPES).filter((brandMediaType) => {
      const channel = brandMediaType.channel ? CHANNELS[brandMediaType.channel] : undefined;
      return (
        (!brandMediaType.featureFlags ||
          (flagStore.ready &&
            brandMediaType.featureFlags.every((flag) => flagStore.flags[flag] === true))) &&
        (!brandMediaType.permissions || identityStore.guard(brandMediaType.permissions)) &&
        (brandMediaType.skipChannelPermissions ||
          !channel?.permissions ||
          identityStore.guard(channel.permissions))
      );
    }),
  );

  const includedBrandMediaTypes = computed(() => {
    const {
      excludedSources = [],
      excludedChannels = [],
      excludedMediaTypes = [],
      excludedBrandMediaTypes = [],
    } = toValue(options);

    return brandMediaTypesWithPermission.value
      .map(({ mediaTypes, ...otherProperties }) => {
        let nextMediaTypes = mediaTypes.filter(
          (mediaType) => !excludedMediaTypes.includes(mediaType),
        );
        if (aiGeneratedVideoOnly.value) {
          nextMediaTypes = restrictToConstellationMediaTypes(
            nextMediaTypes,
            otherProperties.source,
          );
        } else if (
          otherProperties.source === SOURCES.AI_GENERATED.value &&
          canAccessAiVideo.value &&
          !excludedMediaTypes.includes(MEDIA_TYPES.VIDEO.value)
        ) {
          nextMediaTypes = union(nextMediaTypes, [MEDIA_TYPES.VIDEO.value]);
        }
        return { ...otherProperties, mediaTypes: nextMediaTypes };
      })
      .filter(
        (brandMediaType) =>
          brandMediaType.mediaTypes.length > 0 &&
          !excludedBrandMediaTypes.includes(brandMediaType.value) &&
          (!brandMediaType.source || !excludedSources.includes(brandMediaType.source)) &&
          (!brandMediaType.channel || !excludedChannels.includes(brandMediaType.channel)) &&
          (brandMediaType.source !== SOURCES.AI_GENERATED.value || includeAiGenerated.value) &&
          (brandMediaType.source !== SOURCES.COMPETITIVE.value || includeCompetitive.value),
      );
  });

  const includedMediaTypes = computed(() => {
    const uniqueMediaTypes = includedBrandMediaTypes.value.reduce((mediaTypes, brandMediaType) => {
      brandMediaType.mediaTypes.forEach((mediaType) => mediaTypes.add(mediaType));
      return mediaTypes;
    }, new Set<string>());

    return Object.values(MEDIA_TYPES).filter((mediaType) => uniqueMediaTypes.has(mediaType.value));
  });

  const sourceOptions = computed(() =>
    Object.values(SOURCES)
      .filter(
        (source) =>
          // AI_GENERATED is surfaced via its own toggle, only shown as a Sources chip
          // when `includeAiGeneratedSource` is set.
          (source.value !== SOURCES.AI_GENERATED.value || includeAiGeneratedSource.value) &&
          // COMPETITIVE is a real, selectable source on competitor boards
          // (filters posts down to the `*_OTHER` brand media types); on owned
          // channel boards it stays hidden.
          (source.value !== SOURCES.COMPETITIVE.value || includeCompetitive.value),
      )
      .filter((source) =>
        includedBrandMediaTypes.value.some(
          (brandMediaType) => brandMediaType.source === source.value,
        ),
      )
      .map((source) => pick(source, ['label', 'value'])),
  );

  const channelOptions = computed(() => {
    const { sources = [], mediaTypes = [] } = toValue(options);
    return Object.values(CHANNELS)
      .filter((channel) =>
        includedBrandMediaTypes.value.some(
          (brandMediaType) =>
            brandMediaType.channel === channel.value &&
            (!sources.length || sources.includes(brandMediaType.source ?? '')) &&
            (!aiGeneratedVideoOnly.value ||
              !mediaTypes.length ||
              mediaTypes.some((mediaType) => brandMediaType.mediaTypes.includes(mediaType))),
        ),
      )
      .map((channel) => pick(channel, ['label', 'value', 'icon']));
  });

  const mediaTypeOptions = computed(() =>
    Object.values(includedMediaTypes.value).map((mediaType) => pick(mediaType, ['label', 'value'])),
  );

  const brandMediaTypes = computed(() => {
    const { sources = [], channels = [], mediaTypes = [] } = toValue(options);
    const ignoreChannels = channelOptions.value.length === 0;
    return includedBrandMediaTypes.value
      .filter(
        (brandMediaType) =>
          (!sources.length || sources.includes(brandMediaType.source ?? '')) &&
          (ignoreChannels || !channels.length || channels.includes(brandMediaType.channel ?? '')) &&
          (!mediaTypes.length ||
            mediaTypes.some((mediaType) => brandMediaType.mediaTypes.includes(mediaType))),
      )
      .map((brandMediaType) => brandMediaType.value);
  });

  const allEnabledBrandMediaTypes = computed(() =>
    includedBrandMediaTypes.value?.map((brandMediaType) => brandMediaType.value),
  );

  const platforms = computed(() => {
    const { sources = [], channels = [], mediaTypes = [] } = toValue(options);
    return Object.values(PLATFORMS)
      .filter(
        (platform) => includeAiGenerated.value || platform.value !== PLATFORMS.AI_GENERATED.value,
      )
      .filter(
        ({ source, channel, mediaType }) =>
          (!source || !sources.length || sources.includes(source)) &&
          (!channel || !channels.length || channels.includes(channel)) &&
          (!mediaType || !mediaTypes.length || mediaTypes.includes(mediaType)),
      )
      .map((platform) => platform.value);
  });

  const platformFilters = computed(() => {
    const { mediaTypes = [], excludedMediaTypes = [] } = toValue(options);

    let requiredMediaTypes: (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES][] = [];
    if (mediaTypes.length > 0) {
      requiredMediaTypes = includedMediaTypes.value.filter((mediaType) =>
        mediaTypes.includes(mediaType.value),
      );
    } else if (excludedMediaTypes.length > 0 || aiGeneratedVideoOnly.value) {
      // In constellation mode we always emit platform filters so non-AI video never leaks
      // through the coarse brandMediaTypes buckets when no media type is explicitly selected.
      requiredMediaTypes = includedMediaTypes.value;
    }

    return Object.values(requiredMediaTypes).reduce<Record<string, Record<string, unknown>>>(
      (filters, mediaType) => {
        Object.entries(mediaType.platformFilters ?? {})
          .filter(([platform]) => platforms.value.includes(platform))
          // Constellation Projects allow video only from AI-generated media.
          .filter(
            ([platform]) =>
              !aiGeneratedVideoOnly.value ||
              mediaType.value !== MEDIA_TYPES.VIDEO.value ||
              platform === PLATFORMS.AI_GENERATED.value,
          )
          .forEach(([platform, filterObj]) => {
            if (filters[platform] === undefined) {
              filters[platform] = {};
            }
            Object.entries(filterObj).forEach(([filterKey, value]) => {
              if (isArray(value) && isArray(filters[platform][filterKey])) {
                // Merge arrays without duplicates
                filters[platform][filterKey] = union(
                  filters[platform][filterKey] as unknown[],
                  value,
                );
              } else if (
                isBoolean(value) &&
                isBoolean(filters[platform][filterKey]) &&
                filters[platform][filterKey] !== value
              ) {
                // Remove booleans if we're requesting both true and false
                filters[platform] = omit(filters[platform], filterKey);
              } else {
                // Update filter value
                filters[platform][filterKey] = value;
              }
            });
          });
        return filters;
      },
      {},
    );
  });

  return {
    sourceOptions,
    channelOptions,
    mediaTypeOptions,
    brandMediaTypes,
    platformFilters,
    allEnabledBrandMediaTypes,
  };
}
